import React, { useEffect, useState } from 'react';
import type { FC } from 'react';
import { SelectInput, TextInput } from '@wix/cli-ui-kit';
import type { VariantOf } from 'variant';
import { fields, match, variant } from 'variant';
import type { ActionSchema } from '@wix/cli-app-definitions';
import { ComponentType } from '@wix/ambassador-devcenter-apps-v1-app-old/types';
import {
  composeValidations,
  useValidation,
  uuidValidation,
} from '../../../../dev-center/components/validations/index.js';
import { useWixCliAstroBi } from '../../../../bi/index.js';
import { useExtensions } from '../../../extensions/useExtensions.js';

const OpenModalOption = variant({
  Component: fields<{ componentId: string }>(),
  Other: {},
});

type OpenModalOption = VariantOf<typeof OpenModalOption>;

export const OpenModal: FC<{
  onActionSelected: (action: ActionSchema) => void;
}> = ({ onActionSelected }) => {
  const bi = useWixCliAstroBi();
  const { getExtensions } = useExtensions();
  const [componentId, setComponentId] = useState<string>();
  const [shouldUseCustomComponentId, setShouldUseCustomComponentId] =
    useState<boolean>(false);

  const dashboardModals = getExtensions(ComponentType.BACK_OFFICE_MODAL);

  const validateComponentId = useValidation(
    composeValidations([
      [uuidValidation, 'Modal id has to be in the format of a uuid'],
    ])
  );

  useEffect(() => {
    if (componentId) {
      onActionSelected({
        openModal: {
          componentId,
        },
      });
    }
  }, [componentId, onActionSelected]);

  return (
    <>
      <SelectInput<OpenModalOption>
        label={'Choose the modal to open:'}
        info={
          'Choose from a list of available modals in your project or manually add a modal from a different app'
        }
        options={[
          ...dashboardModals.map(({ compId, compData }) => ({
            key: compId,
            title: compData.backOfficeModal.title ?? '',
            value: OpenModalOption.Component({ componentId: compId }),
          })),
          {
            key: 'other',
            title: 'Use a modal from a different app',
            value: OpenModalOption.Other(),
          },
        ]}
        onSubmit={(option) => {
          bi.cliFlowStepAnswered({
            question: 'Choose the modal to open:',
            questionKey:
              'generate_command.dashboard_menu_plugin.what_to_do.open_modal.choose_component',
            answer: option.title,
          });
          return match(option.value, {
            Component: ({ componentId }) => setComponentId(componentId),
            Other: () => setShouldUseCustomComponentId(true),
          });
        }}
      />
      {shouldUseCustomComponentId && (
        <TextInput
          label={'What is the unique id of the modal?'}
          onSubmit={(componentId) => {
            bi.cliFlowStepAnswered({
              question: 'What is the unique id of the modal?',
              questionKey:
                'generate_command.dashboard_menu_plugin.what_to_do.navigate_to_page.choose_page.other.choose_page_id',
              answer: componentId,
            });
            setComponentId(componentId);
          }}
          validate={validateComponentId}
        />
      )}
    </>
  );
};
